import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isAfter, startOfMonth } from 'date-fns';
import {
  LayoutDashboard,
  UserSquare2,
  Briefcase,
  Inbox,
  Receipt,
  FilePlus,
  FileSignature,
  Clock,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { StatCard } from '../../components/shared/StatCard';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { useClients } from '../../core/hooks/useClients';
import { useJobs } from '../../core/hooks/useJobs';
import { useInvoices } from '../../core/hooks/useInvoices';
import { useClientRequests } from '../../core/hooks/useClientRequests';
import { useAuth } from '../../core/contexts/AuthContext';
import { InvoiceStatus } from '../../core/models/Invoice';
import { UserRole } from '../../core/models/User';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/** Open invoices are money already earned that has not arrived yet. */
const OUTSTANDING_STATUSES: InvoiceStatus[] = [
  InvoiceStatus.SENT,
  InvoiceStatus.VIEWED,
  InvoiceStatus.OVERDUE,
];

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, organizationId, clientId } = useAuth();
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;

  const {
    clients: projects,
    loading: projectsLoading,
    error: projectsError,
  } = useClients(organizationId);
  const { jobs, loading: jobsLoading, error: jobsError } = useJobs(
    null,
    organizationId
  );
  const {
    invoices,
    loading: invoicesLoading,
    error: invoicesError,
  } = useInvoices({ organizationId });
  const {
    requests,
    loading: requestsLoading,
    error: requestsError,
  } = useClientRequests(isSuperAdmin ? null : clientId);

  const activeProjects = useMemo(
    () => projects.filter((project) => project.status === 'active'),
    [projects]
  );

  const openJobs = useMemo(
    () =>
      jobs.filter(
        (job) => job.status !== 'completed' && job.status !== 'cancelled'
      ),
    [jobs]
  );

  const pendingRequests = useMemo(
    () => requests.filter((request) => request.status === 'pending'),
    [requests]
  );

  const outstanding = useMemo(
    () =>
      invoices
        .filter((invoice) => OUTSTANDING_STATUSES.includes(invoice.status))
        .reduce((sum, invoice) => sum + (invoice.total || 0), 0),
    [invoices]
  );

  const paidThisMonth = useMemo(() => {
    const monthStart = startOfMonth(new Date());
    return invoices
      .filter(
        (invoice) =>
          invoice.status === InvoiceStatus.PAID &&
          invoice.date &&
          isAfter(invoice.date, monthStart)
      )
      .reduce((sum, invoice) => sum + (invoice.total || 0), 0);
  }, [invoices]);

  const overdueInvoices = useMemo(
    () => invoices.filter((invoice) => invoice.status === InvoiceStatus.OVERDUE),
    [invoices]
  );

  // Recent movement across the things this portal actually tracks, newest
  // first. Derived from real records rather than a hand-written sample.
  const recentActivity = useMemo(() => {
    const entries = [
      ...projects.map((project) => ({
        id: `project-${project.id}`,
        icon: UserSquare2,
        title: project.name,
        context: 'Project updated',
        time: project.updatedAt,
        to: `/clients/${project.id}`,
      })),
      ...jobs.map((job) => ({
        id: `job-${job.id}`,
        icon: Briefcase,
        title: job.title,
        context: `Job ${job.status.replace('-', ' ')}`,
        time: job.updatedAt,
        to: '/jobs',
      })),
      ...invoices.map((invoice) => ({
        id: `invoice-${invoice.id}`,
        icon: Receipt,
        title: `Invoice ${invoice.number}`,
        context: `${currency.format(invoice.total || 0)} · ${invoice.status}`,
        time: invoice.updatedAt,
        to: `/invoices/${invoice.id}`,
      })),
    ];

    return entries
      .filter((entry) => entry.time instanceof Date && !isNaN(+entry.time))
      .sort((a, b) => +b.time - +a.time)
      .slice(0, 6);
  }, [projects, jobs, invoices]);

  const loading =
    projectsLoading || jobsLoading || invoicesLoading || requestsLoading;
  const errors = [
    projectsError && 'projects',
    jobsError && 'jobs',
    invoicesError && 'invoices',
    requestsError && 'client requests',
  ].filter(Boolean) as string[];

  const firstName =
    currentUser?.firstName ||
    currentUser?.displayName?.split(' ')[0] ||
    null;

  const header = (
    <PageHeader
      title={firstName ? `Good to see you, ${firstName}` : 'Dashboard'}
      description={format(new Date(), 'EEEE, d MMMM yyyy')}
      icon={LayoutDashboard}
      actions={
        <Button size="sm" onClick={() => navigate('/tasks')}>
          <Briefcase className="h-4 w-4" aria-hidden="true" />
          Go to tasks
        </Button>
      }
    />
  );

  if (loading) {
    return (
      <div className="space-y-6">
        {header}
        <LoadingState message="Loading your dashboard…" />
      </div>
    );
  }

  if (errors.length === 4) {
    return (
      <div className="space-y-6">
        {header}
        <ErrorState
          title="Couldn't load your dashboard"
          message="None of the dashboard data could be reached. Check your connection and try again."
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header}

      {errors.length > 0 && (
        <div
          role="alert"
          className="rounded-md border border-warning bg-warning/10 p-3 text-sm text-gray-900"
        >
          Some sections could not load ({errors.join(', ')}). The figures below
          exclude them.
        </div>
      )}

      {/* What is true right now. No trend lines: this portal does not yet
          store the historical snapshots a trend would need. */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Active projects"
          value={activeProjects.length}
          icon={UserSquare2}
          description={`of ${projects.length} total`}
        />
        <StatCard
          title="Open jobs"
          value={openJobs.length}
          icon={Briefcase}
          description="not completed or cancelled"
        />
        <StatCard
          title="Pending requests"
          value={pendingRequests.length}
          icon={Inbox}
          description="waiting on your review"
        />
        <StatCard
          title="Outstanding"
          value={currency.format(outstanding)}
          icon={Receipt}
          description={`${currency.format(paidThisMonth)} paid this month`}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Needs attention */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg font-semibold text-primary">
              Needs your attention
            </CardTitle>
            {pendingRequests.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/client-requests')}
              >
                Review all
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {pendingRequests.length === 0 && overdueInvoices.length === 0 ? (
              <div className="flex items-center gap-3 py-6 text-sm text-muted-foreground">
                <CheckCircle2
                  className="h-5 w-5 shrink-0 text-success"
                  aria-hidden="true"
                />
                Nothing is waiting on you. No pending client requests and no
                overdue invoices.
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {pendingRequests.slice(0, 4).map((request) => (
                  <li key={request.id}>
                    <button
                      type="button"
                      onClick={() => navigate('/client-requests')}
                      className="flex w-full items-start gap-3 rounded-md py-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <Inbox
                        className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">
                          {request.title?.trim() || request.description}
                        </span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          Submitted {format(request.createdAt, 'd MMM')}
                        </span>
                      </span>
                      <Badge
                        variant={request.type === 'problem' ? 'warning' : 'default'}
                      >
                        {request.type === 'problem' ? 'Problem' : 'Feature'}
                      </Badge>
                    </button>
                  </li>
                ))}
                {overdueInvoices.slice(0, 3).map((invoice) => (
                  <li key={invoice.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                      className="flex w-full items-start gap-3 rounded-md py-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <Receipt
                        className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">
                          Invoice {invoice.number}
                        </span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          {currency.format(invoice.total || 0)} · due{' '}
                          {format(invoice.dueDate, 'd MMM')}
                        </span>
                      </span>
                      <Badge variant="error">Overdue</Badge>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-primary">
              Start something
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3">
              <Button
                variant="outline"
                className="justify-start"
                onClick={() => navigate('/documents/new')}
              >
                <FilePlus className="h-4 w-4" aria-hidden="true" />
                New document
              </Button>
              <Button
                variant="outline"
                className="justify-start"
                onClick={() => navigate('/envelopes/new')}
              >
                <FileSignature className="h-4 w-4" aria-hidden="true" />
                New signature envelope
              </Button>
              <Button
                variant="outline"
                className="justify-start"
                onClick={() => navigate('/invoices/new')}
              >
                <Receipt className="h-4 w-4" aria-hidden="true" />
                New invoice
              </Button>
              <Button
                variant="outline"
                className="justify-start"
                onClick={() => navigate('/time-tracking')}
              >
                <Clock className="h-4 w-4" aria-hidden="true" />
                Log time
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-primary">
            Recent activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentActivity.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">
              Nothing has changed yet. Activity appears here as projects, jobs,
              and invoices are updated.
            </p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {recentActivity.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    onClick={() => navigate(entry.to)}
                    className="flex w-full items-start gap-3 rounded-md py-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <entry.icon
                      className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">
                        {entry.title}
                      </span>
                      <span className="mt-0.5 block text-xs capitalize text-muted-foreground">
                        {entry.context}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {format(entry.time, 'd MMM, HH:mm')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
